import * as React from 'react';
import { BookOpen, ExternalLink, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@klareospace/pharos-lib-web-utils';

import type { ImportTypeConfig } from '../config';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface DocumentationCardProps {
  config: ImportTypeConfig;
}

// ─── Component ────────────────────────────────────────────────────────────────

/** Documentation panel shown next to the import screen's upload card, driven by `config`. */
export function DocumentationCard({ config }: DocumentationCardProps): React.JSX.Element {
  const { t } = useTranslation('imports');

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-base">
          <BookOpen className="h-5 w-5 text-primary" />
          {t('documentation.title')}
        </CardTitle>
        <CardDescription>{t(config.documentationDescriptionKey)}</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 space-y-4">
        <a
          className="group inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          href={config.documentationUrl}
          rel="noopener noreferrer"
          target="_blank"
        >
          {t('documentation.readMore')}
          <ExternalLink className="h-3.5 w-3.5" />
        </a>

        {config.sections?.map((section) => (
          <div className="space-y-2 rounded-lg border bg-muted/30 p-3" key={section.titleKey}>
            <p className="text-xs font-semibold text-foreground">{t(section.titleKey)}</p>
            <p className="text-xs text-muted-foreground">{t(section.introKey)}</p>
            <ul className="ms-4 list-disc space-y-0.5 text-xs text-muted-foreground">
              {section.itemKeys.map((item) => (
                <li key={item.key}>{t(item.key, item.values)}</li>
              ))}
            </ul>
          </div>
        ))}

        {config.noteKey && (
          <div className="flex items-start gap-2">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <p className="text-xs text-muted-foreground">{t(config.noteKey)}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
